import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, languages, defaultLanguage } from '../i18n';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {

  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('covenant_language');
    if (saved && translations[saved]) {
      return saved;
    }

    const browserLang = navigator.language.split('-')[0];
    if (translations[browserLang]) {
      return browserLang;
    }
    return defaultLanguage;
  });


  useEffect(() => {
    localStorage.setItem('covenant_language', language);

    document.documentElement.lang = language;
  }, [language]);


  const setLanguage = useCallback((newLanguage) => {
    if (translations[newLanguage]) {
      setLanguageState(newLanguage);
    } else {
      console.warn(`Language "${newLanguage}" not supported`);
    }
  }, []);


  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {

        value = translations[defaultLanguage];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {

            console.warn(`Translation key not found: ${key}`);
            return key;
          }
        }
        break;
      }
    }


    if (typeof value !== 'string') {
      return key;
    }


    if (Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }

    return value;
  }, [language]);


  const getTranslations = useCallback((category) => {
    return translations[language]?.[category] || translations[defaultLanguage]?.[category] || {};
  }, [language]);


  const getCurrentLanguage = useCallback(() => {
    return languages.find(l => l.code === language) || languages[0];
  }, [language]);


  const availableLanguages = languages;

  const value = {
    language,
    setLanguage,
    t,
    getTranslations,
    getCurrentLanguage,
    availableLanguages,
    translations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};


export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};


export const useT = () => {
  const { t } = useTranslation();
  return t;
};

export default LanguageContext;